import { useState, useCallback } from "react";
import { X, Search, Plus, Trash2 } from "lucide-react";
import type { LabTestOption } from "../../services/api/admin-order.types";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";

interface LabTestModalProps {
  onClose: () => void;
  onSave: (tests: LabTestOption[]) => void;
  initialTests?: LabTestOption[];
}

const LabTestModal: React.FC<LabTestModalProps> = ({ onClose, onSave, initialTests = [] }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<LabTestOption[]>([]);
  const [selectedTests, setSelectedTests] = useState<LabTestOption[]>(initialTests);
  const [searching, setSearching] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const handleSearch = useCallback(
    (text: string) => {
      setSearch(text);
      if (debounceTimer) clearTimeout(debounceTimer);
      if (!text.trim()) {
        setResults([]);
        return;
      }
      const timer = setTimeout(async () => {
        try {
          setSearching(true);
          const response = await AdminOrderManagementService.searchLabTests(text);
          if (response.success) {
            setResults(response.data.filter((t) => !selectedTests.some((s) => s.id === t.id)));
          }
        } catch (error) {
          console.error("Lab test search error:", error);
        } finally {
          setSearching(false);
        }
      }, 400);
      setDebounceTimer(timer);
    },
    [debounceTimer, selectedTests]
  );

  const addTest = (test: LabTestOption) => {
    setSelectedTests((prev) => [...prev, test]);
    setResults((prev) => prev.filter((t) => t.id !== test.id));
    setSearch("");
    setResults([]);
  };

  const removeTest = (testId: number) => {
    setSelectedTests((prev) => prev.filter((t) => t.id !== testId));
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1000] flex items-center justify-center animate-fadeIn" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-[520px] w-[95%] max-h-[80vh] flex flex-col shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-sm font-bold text-slate-800">Add Lab Tests / Diagnosis Tests</h3>
          <button className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Selected tests */}
        {selectedTests.length > 0 && (
          <div className="px-5 pt-4 pb-2">
            <div className="text-[0.65rem] font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Selected Tests ({selectedTests.length})
            </div>
            <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto">
              {selectedTests.map((test) => (
                <div
                  key={test.id}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-200"
                >
                  {test.name}
                  <button
                    className="w-4 h-4 rounded-full bg-indigo-200 text-indigo-600 flex items-center justify-center hover:bg-red-200 hover:text-red-600 transition-colors border-none cursor-pointer"
                    onClick={() => removeTest(test.id)}
                  >
                    <Trash2 size={9} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="px-5 py-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search lab tests..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-5 pb-3 min-h-0">
          {searching ? (
            <div className="py-6 text-center text-xs text-slate-400">Searching...</div>
          ) : results.length > 0 ? (
            <div className="space-y-1">
              {results.map((test) => (
                <div
                  key={test.id}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-emerald-50 cursor-pointer transition-colors group border border-transparent hover:border-emerald-200"
                  onClick={() => addTest(test)}
                >
                  <div>
                    <div className="text-xs font-semibold text-slate-800">{test.name}</div>
                    <div className="text-[0.6rem] text-slate-400">{test.description}</div>
                  </div>
                  <Plus size={14} className="text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              ))}
            </div>
          ) : search.trim() ? (
            <div className="py-6 text-center text-xs text-slate-400">No tests found</div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
          <button
            className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors border-none cursor-pointer disabled:opacity-40"
            onClick={() => onSave(selectedTests)}
            disabled={selectedTests.length === 0}
          >
            Save Tests ({selectedTests.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default LabTestModal;
